import { addDays, endOfDay, format, startOfDay, subDays } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { SortPayload, useDataProvider } from "react-admin";
import { RegelleistungResourcesEnum } from "shared/enums/resourceMappings";
import useStatusAware from "shared/hooks/useStatusAware";
import { BidViz } from "../types";

export const useBidsData = (selectedDate: Date) => {
  const dataProvider = useDataProvider();
  const { error, loading, setError, setLoading } = useStatusAware();
  const [bids, setBids] = useState<BidViz[]>([]);

  const customFormatDate = useCallback((date: Date) => {
    return format(date, "yyyy-MM-dd");
  }, []);

  const fetchDeliveryDateBids = useCallback(
    async (deliveryDate: Date): Promise<any[]> => {
      let itemsTotal = 0;
      let itemsCount = 0;
      let currentPage = 1;
      let result: any[] = [];

      do {
        const { data, total } = await dataProvider.getList(RegelleistungResourcesEnum.RegelleistungBids, {
          sort: { field: "delivery_date", order: "ASC" } as SortPayload,
          pagination: { page: currentPage, perPage: 100 },
          filter: {
            delivery_date: customFormatDate(deliveryDate),
          },
        });
        itemsCount += data.length;
        itemsTotal = total ?? 0;
        currentPage++;
        result = result.concat(data);
      } while (itemsCount < itemsTotal);

      return result;
    },
    [dataProvider, customFormatDate],
  );

  const fetchAllBids = useCallback(
    async (selDate: Date): Promise<void> => {
      setError(null);
      setLoading(true);
      let allBids: BidViz[] = [];
      const startDate = startOfDay(subDays(selDate, 1));
      const endDate = endOfDay(addDays(selDate, 1));
      let currentDate = startDate;
      try {
        while (currentDate <= endDate) {
          const bids = await fetchDeliveryDateBids(currentDate);
          allBids = allBids.concat(bids);
          currentDate = addDays(currentDate, 1);
        }
        setBids(allBids);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    },
    [fetchDeliveryDateBids, setError, setLoading],
  );

  useEffect(() => {
    fetchAllBids(selectedDate);
  }, [fetchAllBids, selectedDate]);

  return {
    bids,
    loading,
    error,
    customFormatDate,
  };
};
